import { useEffect, useState } from "react";
import axios from "../../../../api/axios";
import { getPollRunSummaryRoute } from "../../../../api/routes";
import CollapsibleHeadedSection from "../../../CollapsibleHeadedSection/CollapsibleHeadedSection";
import { IconJsxer } from "../../../../helpers/IconHelper";
import "./LivePollSelector.scss";

const LivePollSummary = ({ campaignId, streamId, featureId, pollLeaderboardCacheReady }) => {
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryRevision, setSummaryRevision] = useState(0);
    const cacheLoading = pollLeaderboardCacheReady === false;

    useEffect(() => {
        const refreshSummary = ({ detail }) => {
            if (String(detail?.streamID) !== String(streamId) || String(detail?.featureID) !== String(featureId)) return;
            setSummaryRevision((revision) => revision + 1);
        };
        window.addEventListener('dashboard:poll-run-changed', refreshSummary);
        return () => window.removeEventListener('dashboard:poll-run-changed', refreshSummary);
    }, [streamId, featureId]);

    useEffect(() => {
        if (!campaignId || !streamId || !featureId || cacheLoading) {
            setSummary(null);
            setSummaryLoading(cacheLoading);
            return;
        }
        let cancelled = false;
        setSummaryLoading(true);
        axios.get(getPollRunSummaryRoute, { params: { campaignID: campaignId, streamID: streamId, featureID: featureId } })
            .then((response) => {
                if (!cancelled) setSummary(response.data);
            })
            .catch(() => {
                if (!cancelled) setSummary(null);
            })
            .finally(() => {
                if (!cancelled) setSummaryLoading(false);
            });
        return () => { cancelled = true; };
    }, [campaignId, streamId, featureId, cacheLoading, summaryRevision]);

    const openPollData = (poll) => {
        if (!poll?.pollTitle) return;
        window.dispatchEvent(new CustomEvent('dashboard:open-poll-data', {
            detail: { campaignId, streamId, featureId, pollTitle: poll.pollTitle, runId: poll.runId },
        }));
    };

    return <div className="fl-column live-poll-summary-holder">
        <div className="live-control-group-holder">
            <CollapsibleHeadedSection
                preHeaderContent={<div className="group-icon">{IconJsxer.GetIcon(IconJsxer.ICONS.customisationGroup, IconJsxer.ICON_STYLES.standard)}</div>}
                title="Stream Poll Summary"
                open={true}
                canCollapse={false}
            >
                <div className="control-group">
                    <div className="inner-box">
                        <section className="live-poll-summary" aria-live="polite">
            <p className="live-poll-summary-scope">Live totals for polls run on this stream, including votes, awarded correct answers, and the current stream leaderboard.</p>
            {cacheLoading || summaryLoading ? <span>Loading polls...</span> : summary ? <>
                <div className="live-poll-summary-counts">
                    <div className="live-poll-summary-stat"><strong>{summary.pollsRun}</strong><span>Polls run</span></div>
                    <div className="live-poll-summary-stat"><strong>{summary.votesReceived}</strong><span>Votes received</span></div>
                    <div className="live-poll-summary-stat"><strong>{summary.correctVotes}</strong><span>Correct votes</span></div>
                </div>
                <div className="live-poll-summary-highlights">
                    <button type="button" disabled={!summary.mostAnsweredPoll} onClick={() => openPollData(summary.mostAnsweredPoll)}><span>Most answered</span><strong>{summary.mostAnsweredPoll?.pollTitle || 'No votes yet'}</strong>{summary.mostAnsweredPoll && <small>{summary.mostAnsweredPoll.votes} votes · {summary.mostAnsweredPoll.correctVotes} correct</small>}</button>
                    <button type="button" disabled={!summary.mostCorrectlyAnsweredPoll} onClick={() => openPollData(summary.mostCorrectlyAnsweredPoll)}><span>Most correct</span><strong>{summary.mostCorrectlyAnsweredPoll?.pollTitle || 'No correct votes yet'}</strong>{summary.mostCorrectlyAnsweredPoll && <small>{summary.mostCorrectlyAnsweredPoll.votes} votes · {summary.mostCorrectlyAnsweredPoll.correctVotes} correct</small>}</button>
                </div>
                <h6>Top 10 Leaderboard</h6>
                {summary.leaderboard?.length ? <ol>{summary.leaderboard.map((entry, index) => <li key={entry.playerId}><span className="live-poll-summary-rank">{index + 1}</span><span className="live-poll-summary-player">{entry.name}</span><strong>{entry.score}</strong></li>)}</ol> : <span>No leaderboard scores yet.</span>}
            </> : <span>Poll summary is unavailable.</span>}
                        </section>
                    </div>
                </div>
            </CollapsibleHeadedSection>
        </div>
    </div>;
};

export default LivePollSummary;